import { useState, useEffect, useCallback } from 'react';
import {
  Users,
  Share2,
  Heart,
  UserPlus,
  CheckCircle,
  Clock,
  Award,
  ExternalLink,
  Twitter,
  Facebook,
  Instagram,
  Linkedin,
  MessageSquare,
  Send,
  Youtube,
  Music,
  Check,
  X,
  Loader2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../../../context/AuthContext';
import { toast } from 'sonner';
import { ShareOptionsModal } from '../shared/ShareOptionsModal';
import {
  getCommunityTasks,
  submitCommunityTask,
} from '../../../../api/rewards';

/** Same key as Admin → Rewards → Community Rewards; webapp reads only. */
const COMMUNITY_TASKS_PROGRESS_KEY = 'petso_community_tasks_progress';
/** Shared with admin: submissions appear in Admin → Rewards → Community Rewards → Submissions. */

interface AdminCommunityTask {
  id: string;
  title: string;
  description: string;
  category: 'social-follow' | 'social-engagement' | 'referral' | 'community';
  platform?:
    | 'twitter'
    | 'instagram'
    | 'tiktok'
    | 'facebook'
    | 'linkedin'
    | 'telegram'
    | 'whatsapp'
    | 'youtube';
  url?: string;
  reward: number;
  enabled: boolean;
  verificationMethod: 'auto' | 'manual';
  maxParticipants?: number;
  currentParticipants: number;
  targetAudience: string;
  limit: string;
}

interface CommunityTask {
  id: string;
  title: string;
  description: string;
  category: 'social-follow' | 'social-engagement' | 'referral' | 'community';
  platform?:
    | 'twitter'
    | 'instagram'
    | 'tiktok'
    | 'facebook'
    | 'linkedin'
    | 'telegram'
    | 'whatsapp'
    | 'youtube';
  url?: string;
  reward: number;
  status: 'not-started' | 'in-progress' | 'pending' | 'completed';
  verificationMethod: 'auto' | 'manual';
  completedAt?: Date;
  maxParticipants?: number;
  currentParticipants?: number;
}

type TaskStatus = CommunityTask['status'];

function loadProgress(): Record<
  string,
  { status: TaskStatus; completedAt?: string }
> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(COMMUNITY_TASKS_PROGRESS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function saveProgress(
  progress: Record<string, { status: TaskStatus; completedAt?: string }>
): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(
      COMMUNITY_TASKS_PROGRESS_KEY,
      JSON.stringify(progress)
    );
  } catch {
    // ignore
  }
}

export function CommunityRewards() {
  const { t } = useTranslation();
  const { colors, theme } = useTheme();
  const { user } = useAuth();
  const [tasks, setTasks] = useState<CommunityTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState<CommunityTask | null>(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [totalEarned, setTotalEarned] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [taskNotes, setTaskNotes] = useState<Record<string, string>>({});

  const loadTasks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCommunityTasks();
      const progress = loadProgress();

      const merged: CommunityTask[] = res.tasks.map((t) => {
        const p = progress[t.id];
        let status: TaskStatus = (p?.status ?? 'not-started') as TaskStatus;

        // sync with backend status
        if (t.finished) {
          status = 'completed';
        } else if (t.submitted) {
          status = 'pending';
        }

        return {
          id: t.id,
          title: t.title,
          description: t.description,
          category: t.category,
          platform: t.platform,
          url: t.url,
          reward: t.reward,
          status,
          verificationMethod: t.verificationMethod,
          completedAt: p?.completedAt ? new Date(p.completedAt) : undefined,
          maxParticipants: t.maxParticipants,
          currentParticipants: t.currentParticipants,
        };
      });

      setTasks(merged);
      setCompletedCount(res.completed.length);
      setTotalEarned(res.points);
    } catch (err) {
      console.error('Failed to load community tasks:', err);
      toast.error(t('rewards.community.failedToLoadTasks'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadTasks();
  }, [loadTasks]);

  const updateTaskStatus = (
    taskId: string,
    status: TaskStatus,
    completedAt?: Date
  ) => {
    const progress = loadProgress();
    progress[taskId] = { status, completedAt: completedAt?.toISOString() };
    saveProgress(progress);
    setTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status, completedAt } : t))
    );
    if (status === 'completed' && completedAt) {
      const task = tasks.find((t) => t.id === taskId);
      if (task) {
        setCompletedCount((c) => c + 1);
        setTotalEarned((e) => e + task.reward);
      }
    }
  };

  const handleStartTask = (task: CommunityTask) => {
    setSelectedTask(task);
    setShowTaskModal(true);
  };

  const generateReferralLink = () => {
    // In production, this would be a unique referral code from the backend
    const userId = 'user-' + Math.random().toString(36).substr(2, 9);
    return `https://petso.io/signup?ref=${userId}`;
  };

  // Clipboard copy with fallback for all browsers
  const copyToClipboard = async (text: string): Promise<boolean> => {
    // Try modern Clipboard API first
    if (navigator.clipboard && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch {
        console.log('Clipboard API failed, using fallback');
      }
    }

    // Fallback method for older browsers or insecure contexts
    try {
      const textArea = document.createElement('textarea');
      textArea.value = text;

      // Make the textarea invisible and off-screen
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      textArea.style.opacity = '0';

      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      // Try to copy using execCommand
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      return successful;
    } catch (err) {
      console.error('Failed to copy text: ', err);
      return false;
    }
  };

  // Reserved for referral UI actions
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used when referral buttons are wired
  const _handleCopyReferralLink = async () => {
    const link = generateReferralLink();
    const success = await copyToClipboard(link);

    if (success) {
      toast.success(t('rewards.community.referralLinkCopiedToast'));
    } else {
      toast.error(t('rewards.community.failedToCopyLink'));
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- used when share referral buttons are wired
  const _handleShareReferral = async (
    platform: 'whatsapp' | 'telegram' | 'twitter' | 'copy'
  ) => {
    const link = generateReferralLink();
    const message = encodeURIComponent(
      `Join me on Petso - the ultimate pet care platform! Sign up using my link: ${link}`
    );

    let shareUrl = '';
    switch (platform) {
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${message}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent('Join me on Petso - the ultimate pet care platform!')}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${message}`;
        break;
      case 'copy': {
        const success = await copyToClipboard(link);
        if (success) {
          toast.success(t('rewards.community.referralLinkCopiedToast'));
        } else {
          toast.error(t('rewards.community.couldNotCopyLink', { link }), {
            duration: 10000,
          });
        }
        return;
      }
    }

    if (shareUrl) {
      window.open(shareUrl, '_blank');
      if (selectedTask) updateTaskStatus(selectedTask.id, 'in-progress');
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    try {
      if (task.verificationMethod === 'auto') {
        // For auto-verification, we still might want to hit a backend endpoint in the future
        // but for now we follow the existing pattern
        const completedAt = new Date();
        updateTaskStatus(taskId, 'completed', completedAt);
        toast.success(
          t('rewards.community.petsEarnedToast', { count: task.reward })
        );
      } else {
        // Submit to backend for manual verification
        const notes = taskNotes[task.id]?.trim();
        await submitCommunityTask({
          taskId: task.id,
          taskTitle: task.title,
          reward: task.reward,
          evidence: notes || undefined,
        });

        updateTaskStatus(taskId, 'pending');
        toast.info(t('rewards.community.taskSubmittedToast'));
      }
    } catch (err) {
      console.error('Failed to complete task:', err);
      toast.error(t('rewards.community.failedToSubmitTask'));
    }

    setShowTaskModal(false);
    setSelectedTask(null);
  };

  const getPlatformIcon = (platform?: string) => {
    switch (platform) {
      case 'twitter':
        return Twitter;
      case 'instagram':
        return Instagram;
      case 'facebook':
        return Facebook;
      case 'linkedin':
        return Linkedin;
      case 'telegram':
        return Send;
      case 'whatsapp':
        return MessageSquare;
      case 'youtube':
        return Youtube;
      case 'tiktok':
        return Music;
      default:
        return Share2;
    }
  };

  const getStatusBadge = (status: CommunityTask['status']) => {
    switch (status) {
      case 'completed':
        return {
          bg: theme === 'dark' ? 'rgba(34, 197, 94, 0.15)' : '#DCFCE7',
          color: theme === 'dark' ? '#6EE7B7' : '#047857',
          text: t('rewards.community.statusCompleted'),
          icon: CheckCircle,
        };
      case 'pending':
        return {
          bg: theme === 'dark' ? 'rgba(245, 158, 11, 0.15)' : '#FEF3C7',
          color: theme === 'dark' ? '#FCD34D' : '#92400E',
          text: t('rewards.community.statusPendingReview'),
          icon: Clock,
        };
      case 'in-progress':
        return {
          bg: theme === 'dark' ? 'rgba(59, 130, 246, 0.15)' : '#DBEAFE',
          color: theme === 'dark' ? '#60A5FA' : '#1E40AF',
          text: t('rewards.community.statusInProgress'),
          icon: Clock,
        };
      default:
        return {
          bg: theme === 'dark' ? 'rgba(107, 114, 128, 0.15)' : '#F3F4F6',
          color: theme === 'dark' ? '#D1D5DB' : '#4B5563',
          text: t('rewards.community.statusNotStarted'),
          icon: Clock,
        };
    }
  };

  const categories = [
    { id: 'all', name: t('rewards.community.allTasks'), icon: Users },
    {
      id: 'social-follow',
      name: t('rewards.community.socialFollows'),
      icon: UserPlus,
    },
    {
      id: 'social-engagement',
      name: t('rewards.community.engagement'),
      icon: Heart,
    },
    { id: 'referral', name: t('rewards.community.referrals'), icon: Share2 },
  ];

  const [activeCategory, setActiveCategory] = useState('all');

  const filteredTasks =
    activeCategory === 'all'
      ? tasks
      : tasks.filter((t) => t.category === activeCategory);

  const getTaskDisplayTitle = (task: CommunityTask): string => {
    if (task.titleKey) {
      const key = `rewards.community.taskKeys.${task.titleKey}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    if (
      task.platform === 'twitter' &&
      /follow\s+petso\s+on\s+twitter/i.test(task.title)
    ) {
      const key = 'rewards.community.taskKeys.follow_twitter';
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return task.title;
  };

  const getTaskDisplayDescription = (task: CommunityTask): string => {
    if (task.descriptionKey) {
      const key = `rewards.community.taskKeys.${task.descriptionKey}`;
      const translated = t(key);
      if (translated !== key) return translated;
    }
    if (
      task.platform === 'twitter' &&
      /follow\s+petso\s+on\s+twitter/i.test(task.title)
    ) {
      const key = 'rewards.community.taskKeys.follow_twitter_desc';
      const translated = t(key);
      if (translated !== key) return translated;
    }
    return task.description;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px]">
        <Loader2
          className="w-10 h-10 animate-spin mb-4"
          style={{ color: '#594BFF' }}
        />
        <p style={{ color: colors.textSecondary }}>
          {t('rewards.community.loadingTasks') || 'Loading community tasks...'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 min-w-0">
      {/* Header */}
      <div className="min-w-0">
        <h2
          className="text-2xl sm:text-3xl mb-1 sm:mb-2 break-words"
          style={{
            color: colors.heading,
            fontFamily: 'Sora, sans-serif',
            fontWeight: 700,
          }}
        >
          🌟 {t('rewards.community.title')}
        </h2>
        <p
          className="text-base sm:text-lg break-words"
          style={{
            color: colors.textSecondary,
            fontFamily: 'Inter, sans-serif',
          }}
        >
          {t('rewards.community.subtitle')}
        </p>
      </div>

      {/* Stats Cards - stack on mobile to avoid label truncation (e.g. "Available") */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 min-w-0">
        <div
          className="rounded-2xl p-4 sm:p-6 border min-w-0 overflow-hidden"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
              }}
            >
              <Award className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p
                className="text-sm break-words"
                style={{
                  color: colors.textSecondary,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {t('rewards.community.totalEarned')}
              </p>
              <p
                className="text-2xl"
                style={{
                  color: colors.heading,
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
              >
                {totalEarned} PETs
              </p>
            </div>
          </div>
        </div>

        <div
          className="rounded-2xl p-4 sm:p-6 border min-w-0 overflow-hidden"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              }}
            >
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p
                className="text-sm break-words"
                style={{
                  color: colors.textSecondary,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {t('rewards.community.completed')}
              </p>
              <p
                className="text-2xl"
                style={{
                  color: colors.heading,
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
              >
                {completedCount} / {tasks.length}
              </p>
            </div>
          </div>
        </div>

        <div
          className="rounded-2xl p-4 sm:p-6 border min-w-0 overflow-hidden"
          style={{
            backgroundColor: colors.cardBg,
            borderColor: colors.border,
          }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0"
              style={{
                background: 'linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)',
              }}
            >
              <Users className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <p
                className="text-sm break-words"
                style={{
                  color: colors.textSecondary,
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                {t('rewards.community.available')}
              </p>
              <p
                className="text-2xl"
                style={{
                  color: colors.heading,
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
              >
                {tasks.filter((t) => t.status === 'not-started').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Filter - horizontal scroll on mobile */}
      <div
        className="flex gap-2 sm:gap-3 overflow-x-auto overflow-y-hidden pb-1 -mx-1 px-1"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {categories.map((cat) => {
          const Icon = cat.icon;
          const isActive = activeCategory === cat.id;

          return (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className="flex items-center gap-2 px-3 sm:px-4 py-2 rounded-xl transition-all whitespace-nowrap flex-shrink-0 touch-manipulation"
              style={{
                backgroundColor: isActive ? '#594BFF' : colors.surface,
                color: isActive ? '#FFFFFF' : colors.text,
                border: isActive ? 'none' : `1px solid ${colors.border}`,
                fontFamily: 'Sora, sans-serif',
                fontWeight: 600,
              }}
            >
              <Icon className="w-4 h-4" />
              {cat.name}
            </button>
          );
        })}
      </div>

      {/* Tasks Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 min-w-0">
        {filteredTasks.map((task) => {
          const PlatformIcon = getPlatformIcon(task.platform);
          const statusBadge = getStatusBadge(task.status);
          const StatusIcon = statusBadge.icon;

          return (
            <div
              key={task.id}
              className="rounded-2xl p-4 sm:p-6 border transition-all hover:shadow-lg min-w-0 overflow-hidden"
              style={{
                backgroundColor: colors.cardBg,
                borderColor:
                  task.status === 'completed' ? '#10B981' : colors.border,
              }}
            >
              {/* Task Header */}
              <div className="flex items-start justify-between gap-3 mb-4 min-w-0">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor:
                        theme === 'dark'
                          ? 'rgba(89, 75, 255, 0.15)'
                          : '#EEF2FF',
                    }}
                  >
                    <PlatformIcon
                      className="w-6 h-6"
                      style={{ color: '#594BFF' }}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3
                      className="text-base sm:text-lg mb-1 break-words flex items-center gap-1.5"
                      style={{
                        color: colors.heading,
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {getTaskDisplayTitle(task)}
                      {task.url && (
                        <a
                          href={task.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center justify-center w-6 h-6 rounded-lg hover:bg-[#594BFF]/10 transition-colors shrink-0"
                          title="Open task link"
                        >
                          <ExternalLink className="w-4 h-4" style={{ color: '#594BFF' }} />
                        </a>
                      )}
                    </h3>
                    <p
                      className="text-sm break-words"
                      style={{
                        color: colors.textSecondary,
                        fontFamily: 'Inter, sans-serif',
                      }}
                    >
                      {getTaskDisplayDescription(task)}
                    </p>
                  </div>
                </div>

                <div
                  className="px-3 py-1 rounded-lg text-xs flex items-center gap-1"
                  style={{
                    backgroundColor: statusBadge.bg,
                    color: statusBadge.color,
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  <StatusIcon className="w-3 h-3" />
                  {statusBadge.text}
                </div>
              </div>

              {/* Reward */}
              <div
                className="p-3 rounded-xl mb-4"
                style={{
                  backgroundColor:
                    theme === 'dark' ? 'rgba(245, 158, 11, 0.1)' : '#FEF3C7',
                  border: `1px solid ${theme === 'dark' ? 'rgba(245, 158, 11, 0.2)' : '#FDE68A'}`,
                }}
              >
                <div className="flex items-center justify-between">
                  <span
                    className="text-sm"
                    style={{
                      color: theme === 'dark' ? '#FCD34D' : '#92400E',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 500,
                    }}
                  >
                    {t('rewards.community.reward')}
                  </span>
                  <div className="flex items-center gap-2">
                    <Award className="w-5 h-5" style={{ color: '#F59E0B' }} />
                    <span
                      className="text-xl"
                      style={{
                        color: '#F59E0B',
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 700,
                      }}
                    >
                      {task.reward} PETs
                    </span>
                  </div>
                </div>
              </div>

              {/* Action Button */}
              {task.status === 'not-started' && (
                <button
                  onClick={() => handleStartTask(task)}
                  className="w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #594BFF 0%, #8B5CF6 100%)',
                    color: '#FFFFFF',
                    fontFamily: 'Sora, sans-serif',
                  }}
                >
                  {t('rewards.community.startTask')}
                  <ExternalLink className="w-4 h-4" />
                </button>
              )}

              {task.status === 'in-progress' && (
                <div className="space-y-3">
                  <div>
                    <label
                      className="block text-sm font-medium mb-1.5"
                      style={{ color: colors.textSecondary, fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('rewards.community.notesLabel', 'Notes / Reference')}
                    </label>
                    <textarea
                      value={taskNotes[task.id] ?? ''}
                      onChange={(e) =>
                        setTaskNotes((prev) => ({ ...prev, [task.id]: e.target.value }))
                      }
                      placeholder={t(
                        'rewards.community.notesPlaceholder',
                        'Add your username, profile URL, or proof of completion...'
                      )}
                      rows={2}
                      className="w-full rounded-xl px-3 py-2 text-sm resize-none outline-none transition-colors"
                      style={{
                        backgroundColor: theme === 'dark' ? 'rgba(255,255,255,0.06)' : '#F9FAFB',
                        border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.12)' : '#E5E7EB'}`,
                        color: colors.text,
                        fontFamily: 'Inter, sans-serif',
                      }}
                    />
                  </div>
                  <button
                    onClick={() => handleCompleteTask(task.id)}
                    className="w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                    style={{
                      background:
                        'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                      color: '#FFFFFF',
                      fontFamily: 'Sora, sans-serif',
                    }}
                  >
                    <Check className="w-5 h-5" />
                    {t('rewards.community.submitForVerification')}
                  </button>
                </div>
              )}

              {task.status === 'pending' && (
                <div
                  className="w-full py-3 rounded-xl text-center"
                  style={{
                    backgroundColor:
                      theme === 'dark' ? 'rgba(245, 158, 11, 0.1)' : '#FEF3C7',
                    color: theme === 'dark' ? '#FCD34D' : '#92400E',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  ⏳ {t('rewards.community.pendingAdminReview')}
                </div>
              )}

              {task.status === 'completed' && (
                <div
                  className="w-full py-3 rounded-xl text-center flex items-center justify-center gap-2"
                  style={{
                    backgroundColor:
                      theme === 'dark' ? 'rgba(34, 197, 94, 0.1)' : '#DCFCE7',
                    color: '#10B981',
                    fontFamily: 'Sora, sans-serif',
                    fontWeight: 600,
                  }}
                >
                  <CheckCircle className="w-5 h-5" />
                  {t('rewards.community.completedExcl')}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Task Modal */}
      {showTaskModal && selectedTask && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => setShowTaskModal(false)}
        >
          <div
            className="max-w-lg w-full rounded-2xl p-8"
            style={{
              backgroundColor: colors.cardBg,
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-6">
              <h3
                className="text-2xl flex-1"
                style={{
                  color: colors.heading,
                  fontFamily: 'Sora, sans-serif',
                  fontWeight: 700,
                }}
              >
                {selectedTask.title}
              </h3>
              <button
                onClick={() => setShowTaskModal(false)}
                className="p-2 rounded-lg transition-colors"
                style={{
                  color: colors.textSecondary,
                }}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <p
              className="mb-6"
              style={{
                color: colors.text,
                fontFamily: 'Inter, sans-serif',
                lineHeight: 1.6,
              }}
            >
              {selectedTask.description}
            </p>

            {/* Reward Display */}
            <div
              className="p-4 rounded-xl mb-6"
              style={{
                backgroundColor:
                  theme === 'dark' ? 'rgba(245, 158, 11, 0.1)' : '#FEF3C7',
                border: `1px solid ${theme === 'dark' ? 'rgba(245, 158, 11, 0.2)' : '#FDE68A'}`,
              }}
            >
              <div className="flex items-center justify-between">
                <span
                  style={{
                    color: theme === 'dark' ? '#FCD34D' : '#92400E',
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 500,
                  }}
                >
                  {t('rewards.community.youWillEarn')}
                </span>
                <div className="flex items-center gap-2">
                  <Award className="w-6 h-6" style={{ color: '#F59E0B' }} />
                  <span
                    className="text-2xl"
                    style={{
                      color: '#F59E0B',
                      fontFamily: 'Sora, sans-serif',
                      fontWeight: 700,
                    }}
                  >
                    {selectedTask.reward} PETs
                  </span>
                </div>
              </div>
            </div>

            {/* Instructions */}
            <div
              className="p-4 rounded-xl mb-6"
              style={{
                backgroundColor: colors.surface,
                border: `1px solid ${colors.border}`,
              }}
            >
              <h4
                className="font-semibold mb-3"
                style={{
                  color: colors.heading,
                  fontFamily: 'Sora, sans-serif',
                }}
              >
                {t('rewards.community.howToComplete')}
              </h4>
              <ol
                className="list-decimal list-inside space-y-2 text-sm"
                style={{ color: colors.text, fontFamily: 'Inter, sans-serif' }}
              >
                {selectedTask.category === 'social-follow' && (
                  <>
                    <li>
                      {t('rewards.community.instructionFollow1', {
                        platform: selectedTask.platform || 'the platform',
                      })}
                    </li>
                    <li>{t('rewards.community.instructionFollow2')}</li>
                    <li>{t('rewards.community.instructionFollow3')}</li>
                    <li>{t('rewards.community.instructionFollow4')}</li>
                  </>
                )}
                {selectedTask.category === 'social-engagement' && (
                  <>
                    <li>{t('rewards.community.instructionEngage1')}</li>
                    <li>{t('rewards.community.instructionEngage2')}</li>
                    <li>{t('rewards.community.instructionEngage3')}</li>
                    <li>{t('rewards.community.instructionEngage4')}</li>
                  </>
                )}
                {selectedTask.category === 'referral' && (
                  <>
                    <li>{t('rewards.community.instructionReferral1')}</li>
                    <li>{t('rewards.community.instructionReferral2')}</li>
                    <li>{t('rewards.community.instructionReferral3')}</li>
                    <li>{t('rewards.community.instructionReferral4')}</li>
                  </>
                )}
              </ol>
            </div>

            {/* Action Buttons */}
            {selectedTask.category === 'referral' && (
              <div className="space-y-3">
                {/* Single Share Button to Open Modal */}
                <button
                  onClick={() => {
                    updateTaskStatus(selectedTask.id, 'in-progress');
                    setShowShareModal(true);
                  }}
                  className="w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-2"
                  style={{
                    background:
                      'linear-gradient(135deg, #594BFF 0%, #8B5CF6 100%)',
                    color: '#FFFFFF',
                    fontFamily: 'Sora, sans-serif',
                  }}
                >
                  <Share2 className="w-5 h-5" />
                  {t('rewards.community.shareInviteLink')}
                </button>

                <button
                  onClick={() => setShowTaskModal(false)}
                  className="w-full py-3 rounded-xl font-semibold transition-all border"
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: colors.border,
                    color: colors.text,
                    fontFamily: 'Sora, sans-serif',
                  }}
                >
                  {t('common.close')}
                </button>
              </div>
            )}

            {selectedTask.category !== 'referral' && (
              <div className="flex gap-3">
                {selectedTask.url && (
                  <a
                    href={selectedTask.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() =>
                      updateTaskStatus(selectedTask.id, 'in-progress')
                    }
                    className="flex-1 py-4 rounded-xl font-semibold transition-all text-center flex items-center justify-center gap-2"
                    style={{
                      background:
                        'linear-gradient(135deg, #594BFF 0%, #8B5CF6 100%)',
                      color: '#FFFFFF',
                      fontFamily: 'Sora, sans-serif',
                      textDecoration: 'none',
                    }}
                  >
                    <ExternalLink className="w-5 h-5" />
                    {selectedTask.category === 'social-engagement'
                      ? t('rewards.community.viewPostToShare')
                      : t('rewards.community.goToPlatform', {
                          platform: selectedTask.platform || 'Link',
                        })}
                  </a>
                )}

                {!selectedTask.url && (
                  <button
                    onClick={() => {
                      updateTaskStatus(selectedTask.id, 'in-progress');
                      setShowTaskModal(false);
                      toast.info(t('rewards.community.taskStartedToast'));
                    }}
                    className="flex-1 py-4 rounded-xl font-semibold transition-all text-center flex items-center justify-center gap-2"
                    style={{
                      background:
                        'linear-gradient(135deg, #594BFF 0%, #8B5CF6 100%)',
                      color: '#FFFFFF',
                      fontFamily: 'Sora, sans-serif',
                    }}
                  >
                    <Check className="w-5 h-5" />
                    {t('rewards.community.startTask')}
                  </button>
                )}

                <button
                  onClick={() => setShowTaskModal(false)}
                  className="px-6 py-4 rounded-xl font-semibold transition-all border"
                  style={{
                    backgroundColor: 'transparent',
                    borderColor: colors.border,
                    color: colors.text,
                    fontFamily: 'Sora, sans-serif',
                  }}
                >
                  {t('common.close')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && selectedTask && (
        <ShareOptionsModal
          isOpen={showShareModal}
          onClose={() => setShowShareModal(false)}
          title={selectedTask.title}
          message={t('rewards.community.joinMeOnPetso')}
          url={generateReferralLink()}
          shareType="general"
        />
      )}

      {/* Empty State: no tasks from Admin or no tasks in selected category */}
      {filteredTasks.length === 0 && (
        <div className="text-center py-12">
          <Users
            className="w-16 h-16 mx-auto mb-4"
            style={{ color: colors.textTertiary, opacity: 0.3 }}
          />
          <p
            style={{
              color: colors.textSecondary,
              fontFamily: 'Inter, sans-serif',
            }}
          >
            {tasks.length === 0
              ? t('rewards.community.noTasksFromAdmin')
              : t('rewards.community.noTasksInCategory')}
          </p>
        </div>
      )}
    </div>
  );
}
